import "./App.css";
import "./style.css";

import { useState } from "react";

import { api, errorMessage } from "../../api/client";
import {
  attachPhotoToPost,
  fetchPresignedUrls,
  uploadFileToSpaces,
} from "../../api/uploads";
import { useAuth } from "../../auth/AuthContext";
import StatusMessage, {
  errorStatus,
  successStatus,
} from "../StatusMessage/StatusMessage";

// Returns { post_guid, post } so photos can be attached straight away.
function createPostRecord(token, title, content) {
  return api.post("/family/post/create/", token, { title, content });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function RemoveImageButton({ fileName, index, onRemove }) {
  return (
    <button
      onClick={() => onRemove(index)}
      type="button"
      title={`Remove ${fileName}`}
      style={{
        backgroundColor: "#dc2626",
        color: "white",
        border: "none",
        borderRadius: "50%",
        width: "24px",
        height: "24px",
        minWidth: "24px",
        minHeight: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "14px",
        fontWeight: "bold",
        marginLeft: "8px",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        lineHeight: "1",
      }}
      onMouseEnter={(e) => (e.target.style.backgroundColor = "#b91c1c")}
      onMouseLeave={(e) => (e.target.style.backgroundColor = "#dc2626")}
    >
      −
    </button>
  );
}

// Each entry in `uploads` is either:
//   - a raw File object  (while presigning is in-flight), or
//   - { file, presignedUrl, contentType, spacesKey }  (resolved)
function ImageList({ uploads, onRemoveImage }) {
  if (uploads.length === 0) {
    return (
      <div style={{ color: "#6b7280", fontStyle: "italic" }}>no photos or videos</div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {uploads.map((upload, index) => {
        const file = upload.file ?? upload;
        const ready = !!upload.presignedUrl;

        return (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px",
              backgroundColor: ready ? "#f0fdf4" : "#fefce8",
              border: `1px solid ${ready ? "#bbf7d0" : "#fde68a"}`,
              borderRadius: "4px",
              transition: "background-color 0.3s ease",
            }}
          >
            <span style={{ fontSize: "14px" }}>
              {file.name} ({Math.round(file.size / 1024)} KB)
              {!ready && (
                <span style={{ marginLeft: "8px", color: "#92400e" }}>
                  ⏳ getting upload URL…
                </span>
              )}
            </span>
            <RemoveImageButton
              fileName={file.name}
              index={index}
              onRemove={onRemoveImage}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const CreatePost = ({ onCreated }) => {
  const { session } = useAuth();
  const token = session.token;

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [uploads, setUploads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Called every time the user picks a new batch of files.
  // Files are shown immediately; presigned URLs are fetched in the background.
  const addImages = async (event) => {
    const newFiles = Array.from(event.target.files);
    event.target.value = "";

    if (newFiles.length === 0) return;

    const insertionIndex = uploads.length;

    // Show files immediately as unresolved placeholders.
    setUploads((prev) => [...prev, ...newFiles]);

    try {
      const resolved = await fetchPresignedUrls(token, newFiles);

      // Patch exactly the placeholder entries we just inserted.
      setUploads((prev) => {
        const next = [...prev];
        resolved.forEach((r, i) => {
          next[insertionIndex + i] = r;
        });
        return next;
      });
    } catch (err) {
      setMessage(
        errorStatus(
          `Couldn't prepare the selected files: ${errorMessage(err)}`,
        ),
      );

      // Remove the unresolved placeholders so the user can try again.
      setUploads((prev) =>
        prev.filter(
          (_, i) => i < insertionIndex || i >= insertionIndex + newFiles.length,
        ),
      );
    }
  };

  const removeImage = (indexToRemove) => {
    setUploads((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // the submit button is disabled until both of these hold
    if (!title.trim() || uploads.some((u) => !u.presignedUrl)) return;

    setIsLoading(true);
    setMessage(null);

    try {
      // 1. Upload all files to Spaces in parallel.
      await Promise.all(uploads.map((u) => uploadFileToSpaces(u)));

      // 2. Create the post record — Django must return { post_guid: "..." }.
      const { post_guid: postGuid } = await createPostRecord(
        token,
        title,
        content,
      );

      // 3. Attach each uploaded image to the post.
      await Promise.all(
        uploads.map((u) =>
          attachPhotoToPost(token, postGuid, u.spacesKey),
        ),
      );

      setTitle("");
      setContent("");
      setUploads([]);
      setMessage(successStatus("Posted!"));
      onCreated?.();
    } catch (err) {
      setMessage(errorStatus(`Couldn't create the post: ${errorMessage(err)}`));
    } finally {
      setIsLoading(false);
    }
  };

  const allResolved = uploads.every((u) => !!u.presignedUrl);
  const canSubmit = !isLoading && title.trim() && allResolved;

  return (
    <div className="card">
      <h3>Create a new post</h3>

      <form onSubmit={handleSubmit}>
        <div className="new-post-form-title">
          <h4>Title:</h4>
          <input
            type="text"
            name="title"
            maxLength="500"
            required
            id="id_title"
            onChange={(e) => setTitle(e.target.value)}
            value={title}
            disabled={isLoading}
          />
        </div>

        <div className="new-post-content">
          <h4>Content:</h4>
          <div className="ck-editor-container">
            <textarea
              onChange={(e) => setContent(e.target.value)}
              value={content}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="new-post-images">
          <h4>Upload photos or videos:</h4>
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={addImages}
            disabled={isLoading}
          />
        </div>

        <ImageList uploads={uploads} onRemoveImage={removeImage} />

        <StatusMessage message={message} onDismiss={() => setMessage(null)} />

        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          {isLoading ? "Posting…" : !allResolved ? "Preparing uploads…" : "Post"}
        </button>
      </form>
    </div>
  );
};
